import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Store, ShoppingBag, Plus, Search, Package, Clock, DollarSign, Star, Trash2, Edit, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import ImageUploader from "@/components/ImageUploader";
import SponsoredAdWidget from "@/components/SponsoredAdWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Tecnologia", "Design", "Marketing", "Reformas", "Ensino", "Consultoria", "Serviços Gerais", "Outros"];

export default function Services() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [mine, setMine] = useState({ packages: [], purchases: [] });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rateFor, setRateFor] = useState(null); // {package_id, purchase_id, package_title}
  const [rateStars, setRateStars] = useState(5);
  const [rateComment, setRateComment] = useState("");
  const [form, setForm] = useState({ title: "", description: "", price_brl: "", category: "Tecnologia", image_url: "", delivery_days: 7, active: true });

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (cat) params.set("category", cat);
    const [l, m] = await Promise.all([
      axios.get(`${API}/services?${params.toString()}`).catch(() => ({ data: [] })),
      axios.get(`${API}/services/mine`).catch(() => ({ data: { packages: [], purchases: [] } })),
    ]);
    setList(l.data);
    setMine(m.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.title || !form.description || !Number(form.price_brl)) return toast.error("Preencha título, descrição e preço");
    setBusy(true);
    try {
      const payload = { ...form, price_brl: Number(form.price_brl), delivery_days: Number(form.delivery_days) };
      if (editing) {
        await axios.patch(`${API}/services/${editing.package_id}`, payload);
        toast.success("Pacote atualizado!");
      } else {
        await axios.post(`${API}/services`, payload);
        toast.success("Pacote publicado!");
      }
      setShowCreate(false);
      setEditing(null);
      setForm({ title: "", description: "", price_brl: "", category: "Tecnologia", image_url: "", delivery_days: 7, active: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  const del = async (p) => {
    if (!window.confirm(`Excluir pacote "${p.title}"?`)) return;
    try {
      await axios.delete(`${API}/services/${p.package_id}`);
      toast.success("Pacote excluído");
      load();
    } catch { toast.error("Erro"); }
  };

  const buy = async (p) => {
    if (!user) return toast.error("Faça login para comprar");
    if (p.seller_user_id === user.user_id) return toast.error("Você não pode comprar seu próprio pacote");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/services/${p.package_id}/checkout`, { origin_url: `${window.location.origin}` });
      // Add kind param so PaymentSuccess uses the services status endpoint
      toast.info("Redirecionando ao Stripe...");
      const url = new URL(r.data.checkout_url);
      // Instead of tweaking Stripe URL, just rely on success_url from backend then include kind via localStorage marker
      localStorage.setItem("pending_service_session", r.data.session_id);
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title, description: p.description, price_brl: p.price_brl,
      category: p.category, image_url: p.image_url || "", delivery_days: p.delivery_days || 7, active: p.active !== false,
    });
    setShowCreate(true);
  };

  const submitRating = async () => {
    if (!rateFor) return;
    setBusy(true);
    try {
      await axios.post(`${API}/services/${rateFor.package_id}/rate`, {
        purchase_id: rateFor.purchase_id, stars: rateStars, comment: rateComment,
      });
      toast.success("Avaliação enviada!");
      setRateFor(null); setRateStars(5); setRateComment("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <SponsoredAdWidget variant="inline" limit={1} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="services-heading">Marketplace de Serviços</h1>
          <p className="text-muted-foreground mt-1">Pacotes fechados com pagamento seguro via Stripe.</p>
        </div>
        <Button data-testid="services-create-btn" onClick={() => { setEditing(null); setForm({ title: "", description: "", price_brl: "", category: "Tecnologia", image_url: "", delivery_days: 7, active: true }); setShowCreate(true); }} className="rounded-full gap-1">
          <Plus className="w-4 h-4" /> Publicar pacote
        </Button>
      </div>

      <Tabs defaultValue="browse">
        <TabsList className="rounded-full flex flex-wrap gap-1 h-auto p-1">
          <TabsTrigger value="browse" data-testid="services-tab-browse" className="rounded-full text-xs gap-1"><Store className="w-3.5 h-3.5" /> Comprar</TabsTrigger>
          <TabsTrigger value="mine" data-testid="services-tab-mine" className="rounded-full text-xs gap-1"><Package className="w-3.5 h-3.5" /> Meus pacotes ({mine.packages.length})</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="services-tab-purchases" className="rounded-full text-xs gap-1"><ShoppingBag className="w-3.5 h-3.5" /> Vendas & Compras</TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="mt-4 space-y-4">
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="services-search" placeholder="Buscar pacotes..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-9" />
            </div>
            <select data-testid="services-cat" value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 px-3 rounded-md border border-border bg-background text-sm">
              <option value="">Todas categorias</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <Button onClick={load} data-testid="services-search-btn" className="rounded-full">Buscar</Button>
          </div>
          {list.length === 0 && (
            <Card className="p-10 text-center text-muted-foreground">
              <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-40" />
              Nenhum pacote encontrado — que tal ser o primeiro a publicar?
            </Card>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map(p => (
              <Card key={p.package_id} className="overflow-hidden hover-lift" data-testid={`service-${p.package_id}`}>
                <div className="h-36 bg-gradient-to-br from-primary/30 to-purple-500/30 relative">
                  {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />}
                  <Badge className="absolute top-2 right-2 bg-black/70 text-white">{p.category}</Badge>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Avatar className="w-6 h-6"><AvatarImage src={p.seller_picture} /><AvatarFallback className="text-[10px]">{p.seller_name?.[0]}</AvatarFallback></Avatar>
                    <span className="text-xs text-muted-foreground truncate">{p.seller_name}</span>
                  </div>
                  <div className="font-bold text-base leading-tight line-clamp-2">{p.title}</div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{p.description}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.delivery_days} dias</span>
                    {p.purchases_count > 0 && <span className="flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> {p.purchases_count} vendas</span>}
                    {p.rating_avg != null && (
                      <span className="flex items-center gap-1 text-yellow-600 font-semibold" data-testid={`service-rating-${p.package_id}`}>
                        <Star className="w-3 h-3 fill-current" /> {Number(p.rating_avg).toFixed(1)} ({p.rating_count || 0})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="text-xl font-bold text-primary">R$ {Number(p.price_brl).toFixed(2)}</div>
                    <Button size="sm" data-testid={`service-buy-${p.package_id}`} onClick={() => buy(p)} disabled={busy || p.seller_user_id === user?.user_id} className="rounded-full gap-1">
                      <DollarSign className="w-3 h-3" /> Comprar
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="mine" className="mt-4 space-y-4">
          {mine.packages.length === 0 && (
            <Card className="p-8 text-center text-muted-foreground">
              Você ainda não publicou pacotes.
            </Card>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mine.packages.map(p => (
              <Card key={p.package_id} className="p-4 space-y-2" data-testid={`myservice-${p.package_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="font-bold">{p.title}</div>
                    <Badge variant="secondary" className="text-[10px] mt-1">{p.category}</Badge>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-primary">R$ {Number(p.price_brl).toFixed(2)}</div>
                    <div className="text-[10px] text-muted-foreground">{p.purchases_count || 0} vendas</div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">{p.description}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`myservice-edit-${p.package_id}`} className="rounded-full gap-1 flex-1 h-8 text-xs"><Edit className="w-3 h-3" /> Editar</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(p)} data-testid={`myservice-del-${p.package_id}`} className="rounded-full gap-1 h-8 text-xs text-destructive"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="purchases" className="mt-4 space-y-2">
          {mine.purchases.length === 0 && (
            <Card className="p-8 text-center text-muted-foreground">Nenhuma venda ou compra ainda.</Card>
          )}
          {mine.purchases.map(pr => {
            const isSeller = pr.seller_user_id === user?.user_id;
            const canRate = !isSeller && pr.payment_status === "paid";
            return (
              <Card key={pr.purchase_id} className="p-3 flex items-center justify-between gap-3" data-testid={`purchase-${pr.purchase_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{isSeller ? "Venda" : "Compra"}</div>
                  <div className="font-bold text-sm truncate">{pr.package_title}</div>
                  <div className="text-[11px] text-muted-foreground">{new Date(pr.created_at).toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold">R$ {Number(pr.amount).toFixed(2)}</div>
                  <Badge className={`text-[10px] ${pr.payment_status === "paid" ? "bg-green-500" : "bg-amber-500"}`}>{pr.payment_status}</Badge>
                </div>
                {canRate && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full gap-1 h-8 text-xs"
                    data-testid={`purchase-rate-${pr.purchase_id}`}
                    onClick={() => { setRateFor({ package_id: pr.package_id, purchase_id: pr.purchase_id, package_title: pr.package_title }); setRateStars(5); setRateComment(""); }}
                  >
                    <Star className="w-3 h-3" /> Avaliar
                  </Button>
                )}
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      <Dialog open={showCreate} onOpenChange={(v) => { setShowCreate(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-lg" data-testid="services-create-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar pacote" : "Publicar novo pacote"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Título</Label>
              <Input data-testid="service-title" placeholder="Ex: Landing page em 24h" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Descrição</Label>
              <Textarea data-testid="service-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Preço (R$)</Label>
                <Input data-testid="service-price" type="number" min="1" step="0.5" value={form.price_brl} onChange={(e) => setForm({ ...form, price_brl: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Prazo (dias)</Label>
                <Input data-testid="service-days" type="number" min="1" max="180" value={form.delivery_days} onChange={(e) => setForm({ ...form, delivery_days: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Categoria</Label>
              <select data-testid="service-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Imagem de capa</Label>
              <div className="flex gap-2">
                <Input data-testid="service-image" placeholder="URL da imagem" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="flex-1" />
                <ImageUploader testid="service-image-upload" label="Enviar" variant="outline" size="sm" accept="image/*" onUploaded={(url) => setForm({ ...form, image_url: url })} />
              </div>
              {form.image_url && <img src={form.image_url} alt="" className="mt-2 w-full h-32 object-cover rounded" />}
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-primary" data-testid="service-active" />
              Ativo (visível no marketplace)
            </label>
            <div className="text-[11px] text-muted-foreground p-2 rounded bg-primary/5 border border-primary/20">
              💡 Taxa da plataforma: 10% sobre vendas — creditamos o restante direto na sua carteira.
            </div>
            <Button data-testid="service-save" onClick={save} disabled={busy} className="w-full rounded-full">{busy ? "Salvando..." : "Salvar"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rating dialog */}
      <Dialog open={!!rateFor} onOpenChange={(v) => !v && setRateFor(null)}>
        <DialogContent className="max-w-sm" data-testid="service-rate-dialog">
          <DialogHeader><DialogTitle>Avaliar: {rateFor?.package_title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-1 justify-center">
              {[1,2,3,4,5].map(n => (
                <button key={n} data-testid={`service-rate-star-${n}`} onClick={() => setRateStars(n)} className={`text-3xl ${n <= rateStars ? "text-yellow-400" : "text-muted"}`}>
                  ★
                </button>
              ))}
            </div>
            <Textarea data-testid="service-rate-comment" rows={3} placeholder="Como foi sua experiência? (opcional)" value={rateComment} onChange={(e) => setRateComment(e.target.value)} />
            <Button data-testid="service-rate-submit" onClick={submitRating} disabled={busy} className="w-full rounded-full">Enviar avaliação</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
